(function () {
  const selector = '[data-about-audiophiles-humm]';
  const sections = Array.from(document.querySelectorAll(selector));
  const REVEAL_FALLBACK_MS = 3000;
  const REVEAL_FINISH_MS = 780;
  const ENHANCED_CLASS = 'humm-motion-enhanced';

  if (!sections.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  function reveal(section) {
    section.classList.add('is-visible');
  }

  function finishReveal(section) {
    section.classList.remove('is-revealing');
    section.classList.add('is-reveal-forced');
  }

  function forceReveal(section, observer, fallbackTimer) {
    if (fallbackTimer) {
      window.clearTimeout(fallbackTimer);
    }

    if (observer) {
      observer.disconnect();
    }

    reveal(section);

    window.setTimeout(() => {
      finishReveal(section);
    }, REVEAL_FINISH_MS);
  }

  function bindReveal(section) {
    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
      reveal(section);
      finishReveal(section);
      return;
    }

    document.documentElement.classList.add(ENHANCED_CLASS);
    section.classList.add('is-revealing');

    let observer = null;
    let fallbackTimer = null;
    let completed = false;

    const completeReveal = () => {
      if (completed) return;
      completed = true;
      forceReveal(section, observer, fallbackTimer);
    };

    fallbackTimer = window.setTimeout(completeReveal, REVEAL_FALLBACK_MS);

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          completeReveal();
        });
      },
      {
        threshold: 0.35,
      }
    );

    observer.observe(section);
  }

  function resetParallax(media) {
    media.style.setProperty('--aah-parallax-x', '0px');
    media.style.setProperty('--aah-parallax-y', '0px');
  }

  function bindParallax(section) {
    const media = section.querySelector('.about-audiophiles-humm__media');
    const parallaxZone = section.closest('.xo-section') || section;
    if (!media || !canHover.matches || prefersReducedMotion.matches) return;

    let frame = 0;
    let pointer = { x: 0, y: 0 };
    let rect = null;

    function updateRect() {
      rect = parallaxZone.getBoundingClientRect();
    }

    function render() {
      frame = 0;
      if (!rect || section.classList.contains('is-swapping')) return;

      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const moveX = Math.max(-6, Math.min(6, (pointer.x - centerX) * 0.018));
      const moveY = Math.max(-6, Math.min(6, (pointer.y - centerY) * 0.015));

      media.style.setProperty('--aah-parallax-x', `${moveX}px`);
      media.style.setProperty('--aah-parallax-y', `${moveY}px`);
    }

    function requestRender() {
      if (frame) return;
      frame = window.requestAnimationFrame(render);
    }

    parallaxZone.addEventListener(
      'pointerenter',
      (event) => {
        updateRect();
        pointer = { x: event.clientX, y: event.clientY };
        requestRender();
      },
      { passive: true }
    );

    parallaxZone.addEventListener(
      'pointermove',
      (event) => {
        if (section.classList.contains('is-swapping')) return;

        pointer = { x: event.clientX, y: event.clientY };
        requestRender();
      },
      { passive: true }
    );

    parallaxZone.addEventListener('pointerleave', () => {
      resetParallax(media);
      rect = null;
    });

    window.addEventListener('resize', updateRect);
  }

  function getStackImages(section) {
    return Array.from(section.querySelectorAll('[data-aah-image]'));
  }

  function positionOf(image) {
    if (image.classList.contains('about-audiophiles-humm__image--front')) return 'front';
    if (image.classList.contains('about-audiophiles-humm__image--back-right')) return 'back-right';
    return 'back-left';
  }

  function applyPosition(image, position) {
    image.classList.remove(
      'about-audiophiles-humm__image--front',
      'about-audiophiles-humm__image--back-right',
      'about-audiophiles-humm__image--back-left'
    );
    image.classList.add(`about-audiophiles-humm__image--${position}`);

    if (position === 'front') {
      image.setAttribute('aria-label', 'Front image');
      image.setAttribute('tabindex', '-1');
    } else {
      image.setAttribute('aria-label', 'Bring image to front');
      image.removeAttribute('tabindex');
    }
  }

  function endSwap(section, images, fallbackTimer) {
    if (fallbackTimer) {
      window.clearTimeout(fallbackTimer);
    }

    section.classList.remove('is-swapping');
    images.forEach((image) => {
      image.style.willChange = '';
    });
  }

  function bindStackControls(section) {
    const media = section.querySelector('.about-audiophiles-humm__media');
    const images = getStackImages(section);
    if (images.length < 2) return;

    const positionOrder = ['front', 'back-right', 'back-left'];

    images
      .sort((a, b) => positionOrder.indexOf(positionOf(a)) - positionOrder.indexOf(positionOf(b)))
      .forEach((image, index) => {
        applyPosition(image, positionOrder[index] || 'back-left');
      });

    images.forEach((image) => {
      image.addEventListener('click', () => {
        if (image.classList.contains('about-audiophiles-humm__image--front')) return;
        if (section.classList.contains('is-swapping')) return;

        const currentOrder = getStackImages(section).sort(
          (a, b) => positionOrder.indexOf(positionOf(a)) - positionOrder.indexOf(positionOf(b))
        );
        const nextOrder = [image].concat(currentOrder.filter((item) => item !== image));

        section.classList.add('is-swapping');
        if (media) {
          resetParallax(media);
        }

        images.forEach((item) => {
          item.style.willChange = 'transform';
        });

        nextOrder.forEach((item, index) => {
          applyPosition(item, positionOrder[index] || 'back-left');
        });

        let pending = images.length;
        let fallbackTimer = window.setTimeout(() => {
          endSwap(section, images, fallbackTimer);
        }, 340);

        const onTransitionEnd = (event) => {
          if (event.propertyName !== 'transform') return;

          pending -= 1;
          if (pending <= 0) {
            images.forEach((item) => {
              item.removeEventListener('transitionend', onTransitionEnd);
            });
            endSwap(section, images, fallbackTimer);
          }
        };

        if (prefersReducedMotion.matches) {
          endSwap(section, images, fallbackTimer);
          return;
        }

        images.forEach((item) => {
          item.addEventListener('transitionend', onTransitionEnd);
        });
      });
    });
  }

  function setup(section) {
    if (section.dataset.aahBound === '1') return;

    section.dataset.aahBound = '1';

    const motionEnabled = section.dataset.aahMotion !== 'false';

    if (motionEnabled) {
      bindReveal(section);
    }

    bindStackControls(section);
    bindParallax(section);
  }

  sections.forEach(setup);

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target || !event.target.querySelectorAll) return;

    event.target.querySelectorAll(selector).forEach(setup);
  });
})();
